import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useParams } from "react-router-dom";

import { isCompanyFinanceEnabled } from "../../../config/companyFinance";
import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import { messageFromError } from "../../../shared/api/drfErrors";
import {
  fetchCompany,
  fetchMyCompany,
} from "../../companyWorkspace/api/companyWorkspace";
import {
  canAccessCompanyFinanceBooks,
  membershipGenerationOf,
} from "../../companyWorkspace/lib/companyFinanceLabels";
import {
  isActiveMembership,
  isOwnerMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import CompanyPageHeader from "../../companyWorkspace/ui/CompanyPageHeader";
import { fetchMetricCount } from "../api/companyOverviewMetrics";
import {
  buildOverviewMetricPlan,
  canSeeManagementClientQueue,
  canSeeManagementPropertyQueue,
} from "../lib/overviewMetrics";

function httpStatus(err) {
  return err?.response?.status ?? null;
}

function isFailClosedOptional(err) {
  const status = httpStatus(err);
  return status === 403 || status === 404;
}

function MetricCardView({ metric }) {
  const valueLabel =
    metric.status === "ok"
      ? metric.value == null
        ? "—"
        : String(metric.value)
      : metric.status === "loading"
        ? "…"
        : "Недоступно";

  return (
    <article
      className="feature-dashboard__metric-card feature-company-overview__metric-card"
      data-testid={`company-overview-metric-${metric.id}`}
      data-metric-status={metric.status}
    >
      <span className="feature-dashboard__metric-label">{metric.label}</span>
      <span
        className="feature-dashboard__metric-value"
        data-testid={`company-overview-value-${metric.id}`}
      >
        {valueLabel}
      </span>
      <p className="feature-company-overview__metric-desc">{metric.description}</p>
      {metric.status === "error" ? (
        <p className="feature-company-overview__metric-error" role="status">
          Не вдалося завантажити показник
        </p>
      ) : null}
      <Link
        to={metric.drillDown}
        className="feature-company-overview__metric-link"
        data-testid={`company-overview-drill-${metric.id}`}
      >
        Переглянути →
      </Link>
    </article>
  );
}

function CompanyOperationalOverviewContent() {
  const { companyId } = useParams();
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const snapshotRef = useRef(null);
  const authorityRef = useRef({
    companyId: null,
    actorId: null,
    generation: null,
  });

  const [company, setCompany] = useState(null);
  const [membership, setMembership] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [authorityCompanyId, setAuthorityCompanyId] = useState(null);
  const [pageState, setPageState] = useState("loading");
  const [pageError, setPageError] = useState(null);
  const [metrics, setMetrics] = useState([]);
  const [staleBanner, setStaleBanner] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    snapshotRef.current = { metrics, company, membership };
  }, [metrics, company, membership]);

  useEffect(() => {
    authorityRef.current = {
      companyId: authorityCompanyId,
      actorId: authorityActorId,
      generation: membershipGenerationOf(company, membership),
    };
  }, [authorityCompanyId, authorityActorId, company, membership]);

  const clearProtected = useCallback(() => {
    setCompany(null);
    setMembership(null);
    setAuthorityActorId(null);
    setAuthorityCompanyId(null);
    setMetrics([]);
    setStaleBanner(null);
    setPageError(null);
  }, []);

  const load = useCallback(
    async ({ isRefresh = false } = {}) => {
      if (!companyId) {
        setPageState("not-found");
        return;
      }

      let op = scopeGate.beginRead({
        companyId,
        actorId,
      });

      if (!isRefresh) {
        setPageState("loading");
        setPageError(null);
        setStaleBanner(null);
      } else {
        setRefreshing(true);
      }

      try {
        const [companyData, mineData] = await Promise.all([
          fetchCompany(companyId),
          fetchMyCompany(),
        ]);

        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }

        const mineCompany = mineData?.company || null;
        const mineMembership = mineData?.membership || null;
        const mineMatches =
          mineCompany != null &&
          sameCompanyId(mineCompany.id, companyId) &&
          isActiveMembership(mineCompany, mineMembership);

        if (!mineMatches) {
          clearProtected();
          setPageState("forbidden");
          setPageError("Огляд компанії доступний лише активним учасникам.");
          return;
        }

        const effectiveCompany = {
          ...companyData,
          ...mineCompany,
          id: companyData?.id ?? mineCompany.id,
        };
        const effectiveMembership = mineMembership;
        const generation = membershipGenerationOf(
          effectiveCompany,
          effectiveMembership,
        );

        scopeGate.bindIdentity({
          companyId,
          actorId,
          membershipGeneration: generation,
          bindMembershipGeneration: true,
        });

        op = scopeGate.beginRead({
          companyId,
          actorId,
          membershipGeneration: generation,
        });

        const financeEnabled = isCompanyFinanceEnabled();
        const plan = buildOverviewMetricPlan(
          effectiveCompany,
          effectiveMembership,
          {
            financeEnabled,
            canAccessFinanceBooks: canAccessCompanyFinanceBooks(
              effectiveCompany,
              effectiveMembership,
            ),
            canSeeClientQueue: canSeeManagementClientQueue(
              effectiveCompany,
              effectiveMembership,
            ),
            canSeePropertyQueue: canSeeManagementPropertyQueue(
              effectiveCompany,
              effectiveMembership,
            ),
          },
        );

        const initialCards = plan.map((item) => ({
          ...item,
          status: item.kind === "company_field" ? "ok" : "loading",
          value:
            item.kind === "company_field"
              ? effectiveCompany[item.field] ?? null
              : null,
        }));

        setCompany(effectiveCompany);
        setMembership(effectiveMembership);
        setAuthorityActorId(actorId);
        setAuthorityCompanyId(companyId);
        setMetrics(initialCards);
        setPageState("ready");
        setPageError(null);

        const settled = await Promise.allSettled(
          plan
            .filter((item) => item.kind === "paginated")
            .map(async (item) => {
              const value = await fetchMetricCount(item.source, companyId);
              return { id: item.id, value, finance: item.finance === true };
            }),
        );

        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }

        const omitIds = new Set();
        const nextById = new Map();
        const paginated = plan.filter((m) => m.kind === "paginated");

        // R1: any finance 403/404 => omit the entire finance metric group.
        let financeAuthorityDenied = false;
        settled.forEach((result, index) => {
          const item = paginated[index];
          if (!item || !item.finance) return;
          if (
            result.status === "rejected" &&
            isFailClosedOptional(result.reason)
          ) {
            financeAuthorityDenied = true;
          }
        });
        if (financeAuthorityDenied) {
          paginated.forEach((item) => {
            if (item.finance) omitIds.add(item.id);
          });
        }

        settled.forEach((result, index) => {
          const item = paginated[index];
          if (!item) return;
          if (omitIds.has(item.id)) return;
          if (result.status === "fulfilled") {
            nextById.set(item.id, {
              status: "ok",
              value: result.value.value,
            });
            return;
          }
          const err = result.reason;
          if (item.optionalAuthority && isFailClosedOptional(err)) {
            omitIds.add(item.id);
            return;
          }
          nextById.set(item.id, {
            status: "error",
            value: null,
          });
        });

        setMetrics((prev) =>
          prev
            .filter((card) => {
              if (omitIds.has(card.id)) return false;
              // Drop prior-scope finance siblings if current load denied finance.
              if (financeAuthorityDenied && card.finance) return false;
              return true;
            })
            .map((card) => {
              if (card.kind === "company_field") {
                return {
                  ...card,
                  status: "ok",
                  value: effectiveCompany[card.field] ?? null,
                };
              }
              const update = nextById.get(card.id);
              if (!update) return card;
              return { ...card, ...update };
            }),
        );
        setStaleBanner(null);
      } catch (err) {
        if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
          return;
        }
        const message = messageFromError(err);
        const auth = authorityRef.current;
        const sameScopeSnapshot =
          isRefresh &&
          snapshotRef.current?.metrics?.length > 0 &&
          sameCompanyId(auth.companyId, companyId) &&
          sameActorId(auth.actorId, actorId);

        if (sameScopeSnapshot) {
          setStaleBanner(
            `Не вдалося оновити дані: ${message || "помилка мережі"}. Показано попередні значення.`,
          );
          setPageState("stale");
        } else {
          clearProtected();
          setPageError(message);
          setPageState("error");
        }
      } finally {
        if (mountedRef.current) {
          setRefreshing(false);
        }
      }
    },
    [actorId, clearProtected, companyId, scopeGate],
  );

  useEffect(() => {
    mountedRef.current = true;
    clearProtected();
    scopeGate.bindIdentity({
      companyId,
      actorId,
    });
    void load({ isRefresh: false });
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
    };
  }, [companyId, actorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const companyMatches =
    company != null && sameCompanyId(company.id, companyId);
  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const authorityMatches = companyMatches && actorMatches;
  const archived = company?.status === "archived";
  const owner = isOwnerMembership(company, membership);

  if (pageState === "loading" || (pageState === "ready" && !authorityMatches)) {
    return (
      <div className="feature-dashboard__state feature-company-overview__state">
        <Spinner size="lg" label="Завантаження огляду компанії" />
      </div>
    );
  }

  if (
    pageState === "error" ||
    pageState === "forbidden" ||
    pageState === "not-found"
  ) {
    return (
      <div className="feature-dashboard__state feature-company-overview__state">
        <EmptyState
          icon="fa-solid fa-triangle-exclamation"
          title="Не вдалося завантажити огляд"
          description={pageError || "Спробуйте оновити сторінку."}
          size="md"
          primaryAction={(
            <button
              type="button"
              className="action-btn"
              onClick={() => void load({ isRefresh: false })}
            >
              Спробувати знову
            </button>
          )}
        />
      </div>
    );
  }

  return (
    <section
      className="clients-page feature-company-workspace feature-company-overview"
      id="Csection"
      tabIndex="-1"
      data-testid="company-operational-overview"
    >
      <div className="container">
        <CompanyPageHeader
          title="Огляд компанії"
          subtitle="Операційні показники з корпоративних джерел. Особисті записи не входять у ці числа."
          company={company}
          membership={membership}
          companyId={companyId}
          activeNav="overview"
          actions={
            <button
              type="button"
              className="action-btn"
              data-testid="company-overview-refresh"
              onClick={() => void load({ isRefresh: true })}
              disabled={refreshing}
            >
              <i className="fa-solid fa-rotate-right" aria-hidden="true" />
              {refreshing ? "Оновлення…" : "Оновити"}
            </button>
          }
        />

        {archived ? (
          <div
            className="feature-company-workspace__banner"
            role="status"
            data-testid="company-overview-archived-banner"
          >
            Компанія в архіві. Огляд лише для читання.
            {owner
              ? " Звичайні зміни закриті; підтримувані дії доступні в інших розділах."
              : null}
          </div>
        ) : null}

        {pageState === "stale" && staleBanner ? (
          <div
            className="feature-dashboard__stale-banner"
            role="status"
            data-testid="company-overview-stale-banner"
          >
            <span>{staleBanner}</span>
            <button
              type="button"
              className="action-btn"
              onClick={() => void load({ isRefresh: true })}
            >
              Спробувати знову
            </button>
          </div>
        ) : null}

        <div className="feature-dashboard feature-company-overview__body">
          <section
            className="feature-dashboard__section"
            aria-label="Операційні показники"
          >
            <h2 className="feature-dashboard__section-title">Показники</h2>
            <div
              className="feature-dashboard__metric-grid feature-company-overview__metric-grid"
              data-testid="company-overview-metric-grid"
            >
              {metrics.map((metric) => (
                <MetricCardView key={metric.id} metric={metric} />
              ))}
            </div>
          </section>
        </div>
      </div>
    </section>
  );
}

export default CompanyOperationalOverviewContent;
